/**
 * Pure matching/merge logic for the CSV contact import (Wave 2 of the
 * 2026-06-26 UAT round-2 plan). Kept free of Supabase/React so vitest can
 * cover it directly. The merge semantics mirror Forge's people importer
 * (docs/superpowers/plans/2026-05-16-comprehensive-people-import.md),
 * adapted to relay.contacts' unique constraints:
 *  - UNIQUE (organization_id, email) spans soft-deleted rows;
 *  - partial UNIQUE (organization_id, phone) covers active rows only.
 * Those constraints are also why relay offers skip / update-fill-blanks but
 * no "create anyway" — the database rejects true duplicates.
 */

export type DuplicateMode = "skip" | "update";

/** Minimal projection used to index existing contacts for dupe matching. */
export interface MatchableContact {
  id: string;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  deleted_at: string | null;
}

export interface MatchIndex {
  /**
   * normalized (lowercased) email -> contact, or null when two DIFFERENT
   * contacts differ only by email casing (possible: the DB constraint is on
   * the RAW value) — an ambiguous email must never match. Soft-deleted rows
   * are indexed too when visible, because UNIQUE (organization_id, email)
   * spans them — but RLS hides deleted rows from client fetches, so the
   * import's insert path must ALSO map that unique violation to a skip.
   */
  byEmail: Map<string, MatchableContact | null>;
  /**
   * normalized phone digits -> ACTIVE contact (phone + mobile indexed), or
   * null when two DIFFERENT contacts share the digits — an ambiguous phone
   * must never match (guessing would enrich/skip an arbitrary record).
   */
  byPhone: Map<string, MatchableContact | null>;
}

/** Mirrors the import loop's identifier gate: a row needs a name or email. */
export function rowHasIdentifier(row: {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): boolean {
  return Boolean(row.email?.trim() || row.first_name?.trim() || row.last_name?.trim());
}

export function normalizeEmailForMatch(v: string | null | undefined): string | null {
  const s = (v ?? "").trim().toLowerCase();
  return s.length > 0 ? s : null;
}

/**
 * Digits-only phone canonicalization for matching. The column is free-form
 * text (import stores trimmed user input as-is), so CSV and stored rows
 * rarely agree on formatting. US 11-digit numbers drop the leading 1 so
 * "+1 (555) 010-2030" matches "5550102030". Fewer than 7 digits is treated
 * as non-identifying.
 */
export function normalizePhoneForMatch(v: string | null | undefined): string | null {
  const digits = (v ?? "").replace(/\D/g, "");
  if (digits.length < 7) return null;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

/**
 * Register one contact into the index. Also used mid-import for just-created
 * contacts, so later rows of the same CSV match them (same-batch dedupe).
 */
export function registerContact(index: MatchIndex, c: MatchableContact): void {
  const email = normalizeEmailForMatch(c.email);
  if (email) {
    const existing = index.byEmail.get(email);
    if (existing === undefined) {
      index.byEmail.set(email, c);
    } else if (existing !== null && existing.id !== c.id) {
      // Two different contacts case-fold to the same email — poison the
      // entry so email matching never guesses between them.
      index.byEmail.set(email, null);
    }
  }
  if (c.deleted_at) return; // phone matching considers active rows only
  for (const raw of [c.phone, c.mobile]) {
    const phone = normalizePhoneForMatch(raw);
    if (!phone) continue;
    const existing = index.byPhone.get(phone);
    if (existing === undefined) {
      index.byPhone.set(phone, c);
    } else if (existing !== null && existing.id !== c.id) {
      // Two different contacts share these digits — poison the entry so
      // phone matching never guesses between them.
      index.byPhone.set(phone, null);
    }
  }
}

export function buildMatchIndex(contacts: MatchableContact[]): MatchIndex {
  const index: MatchIndex = { byEmail: new Map(), byPhone: new Map() };
  for (const c of contacts) registerContact(index, c);
  return index;
}

export type RowMatchResult =
  | { kind: "match"; contact: MatchableContact; matchedBy: "email" | "phone" }
  /**
   * The row's identifier is shared by MULTIPLE existing contacts. Callers
   * must neither enrich (wrong-record risk) nor insert (the "duplicates are
   * never created" policy — a third record with the same identifier in a
   * new format would slip past the raw-value unique indexes).
   */
  | { kind: "ambiguous"; matchedBy: "email" | "phone" }
  | { kind: "none" };

/** Email first, then phone/mobile — the UAT plan's match precedence. */
export function matchRow(
  row: { email?: string | null; phone?: string | null; mobile?: string | null },
  index: MatchIndex,
): RowMatchResult {
  const email = normalizeEmailForMatch(row.email);
  if (email && index.byEmail.has(email)) {
    const hit = index.byEmail.get(email);
    return hit
      ? { kind: "match", contact: hit, matchedBy: "email" }
      : { kind: "ambiguous", matchedBy: "email" };
  }
  for (const raw of [row.phone, row.mobile]) {
    const phone = normalizePhoneForMatch(raw);
    if (phone && index.byPhone.has(phone)) {
      const hit = index.byPhone.get(phone);
      return hit
        ? { kind: "match", contact: hit, matchedBy: "phone" }
        : { kind: "ambiguous", matchedBy: "phone" };
    }
  }
  return { kind: "none" };
}

const isBlank = (v: unknown): boolean =>
  v == null || (typeof v === "string" && v.trim().length === 0);

/**
 * Update-fill-blanks patch (Forge merge.ts parity): a field is written only
 * when the incoming value is non-blank AND the existing value is blank.
 * Existing data is never overwritten and never nulled from a blank cell.
 */
export function computeFillOnlyPatch(
  existing: Record<string, unknown>,
  incoming: Record<string, unknown>,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(incoming)) {
    if (isBlank(value)) continue;
    if (!isBlank(existing[key])) continue;
    patch[key] = value;
  }
  return patch;
}

/**
 * "Jane Q. Doe" -> { first_name: "Jane", last_name: "Q. Doe" };
 * "Doe, Jane" (export-style) -> { first_name: "Jane", last_name: "Doe" }.
 * Single tokens land in first_name. Deliberately simpler than Forge's
 * church-flavored splitter (no suffix/nickname/deceased handling) — Relay
 * CSVs are CRM exports, and mis-guessing silently corrupts names.
 */
export function splitFullName(full: string): {
  first_name: string | null;
  last_name: string | null;
} {
  const trimmed = full.trim();
  if (trimmed.length === 0) return { first_name: null, last_name: null };

  const commaAt = trimmed.indexOf(",");
  if (commaAt > 0) {
    const last = trimmed.slice(0, commaAt).trim();
    const first = trimmed.slice(commaAt + 1).trim();
    return { first_name: first || null, last_name: last || null };
  }

  const tokens = trimmed.split(/\s+/);
  if (tokens.length === 1) return { first_name: tokens[0], last_name: null };
  return { first_name: tokens[0], last_name: tokens.slice(1).join(" ") };
}
